// Dynamic public-portal section primitives.
// Each section is data-driven via contentService (geo-api). Sections render
// nothing when there is no published content, so the homepage gracefully
// adapts to admin-controlled content state without redesigning layouts.
import React from 'react';
import { Link } from 'react-router-dom';
import { safeUrl } from '@/lib/urlSafety';
import {
  ArrowRight,
  Bell,
  PlayCircle,
  FileText,
  Map as MapIcon,
  MapPin,
  Users,
  ShieldCheck,
  Leaf,
  Building2,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import type {
  PublicNotice,
  PublicVideo,
  Publication,
  GisShowcaseItem,
  OfficeContact,
  TeamProfile,
  ExecutiveMessage,
  EnvironmentalNotice,
  PricingPlan,
  PublishStatus,
} from '@/services/contentService';

const statusBadge = (status: PublishStatus): { label: string; cls: string } => {
  switch (status) {
    case 'published':
      return { label: 'Published', cls: 'bg-eds-success/15 text-eds-emerald-2 border-eds-success/30' };
    case 'approved':
      return { label: 'Approved', cls: 'bg-eds-info/12 text-eds-info border-eds-info/30' };
    case 'in_review':
      return { label: 'In Review', cls: 'bg-eds-gold/15 text-eds-earth border-eds-gold/30' };
    case 'draft':
      return { label: 'Draft', cls: 'bg-eds-surface-2 text-eds-fg border-eds-border' };
    case 'archived':
      return { label: 'Archived', cls: 'bg-eds-danger/10 text-eds-danger border-eds-danger/30' };
  }
};

export const PreviewBadge: React.FC<{ status: PublishStatus }> = ({ status }) => {
  if (status === 'published') return null;
  const b = statusBadge(status);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-eds-pill border ${b.cls}`}>
      <Eye className="w-3 h-3" /> {b.label}
    </span>
  );
};

export const PreviewBanner: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  if (!enabled) return null;
  return (
    <div className="bg-eds-gold text-eds-earth text-xs font-semibold text-center py-1.5 px-4">
      Preview mode. Only published content is shown — publication is controlled by the server.
    </div>
  );
};

// ─── Announcements / notices ────────────────────────────────────────────────
export const AnnouncementsSection: React.FC<{ items: PublicNotice[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-12 bg-eds-gold/15 border-y border-eds-gold/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-6">
          <Bell className="w-5 h-5 text-eds-earth" />
          <h2 className="text-2xl font-bold text-eds-fg">Announcements & Notices</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((n) => (
            <div key={n.id} className="bg-eds-surface border border-eds-gold/30 rounded-eds-md p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-eds-earth font-semibold">
                  {n.severity ?? 'info'}
                </span>
                <PreviewBadge status={n.publishStatus} />
              </div>
              <h3 className="font-semibold text-eds-fg">{n.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-1 line-clamp-3">{n.body}</p>
              <div className="text-xs text-eds-muted-fg mt-3">{n.publishedAt}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Dynamic services ───────────────────────────────────────────────────────
export const ServicesSection: React.FC<{ items: { id: string; title: string; description: string; publishStatus: PublishStatus }[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-eds-fg">Information and Services</h2>
          <p className="text-eds-muted-fg mt-2">Supporting geological information management, investor engagement, GIS operations, regulatory services, and resource development activities.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => (
            <div key={s.id} className="bg-eds-surface border border-eds-border rounded-eds-md p-6 hover:shadow-eds-md transition">
              <div className="flex items-center justify-between mb-2">
                <ShieldCheck className="w-6 h-6 text-eds-emerald-2" />
                <PreviewBadge status={s.publishStatus} />
              </div>
              <h3 className="font-semibold text-eds-fg">{s.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-2">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Dynamic features ───────────────────────────────────────────────────────
export const FeaturesSection: React.FC<{ items: { id: string; title: string; description: string; publishStatus: PublishStatus }[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-eds-fg">Platform Services</h2>
          <p className="text-eds-muted-fg mt-2">The GIISDEC platform provides secure access to approved information, services, and digital workflows.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((f) => (
            <div key={f.id} className="bg-eds-surface border border-eds-border rounded-eds-md p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="w-2 h-2 rounded-eds-pill bg-eds-emerald-2" />
                <PreviewBadge status={f.publishStatus} />
              </div>
              <h3 className="font-semibold text-eds-fg">{f.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-1">{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Dynamic pricing (with governance status) ───────────────────────────────
export const PricingSection: React.FC<{ plans: PricingPlan[] }> = ({ plans }) => {
  if (!plans.length) return null;
  return (
    <section className="py-16 bg-eds-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-eds-fg">Access Levels</h2>
          <p className="text-eds-muted-fg mt-2">Who can use each part of the platform. Access is governed server-side by GIISDEC — these are not commercial plans.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`rounded-eds-lg border p-6 bg-eds-surface ${
                p.highlighted ? 'border-eds-emerald ring-2 ring-eds-emerald/30' : 'border-eds-border'
              }`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-eds-fg">{p.name}</h3>
                <PreviewBadge status={p.publishStatus} />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-eds-fg">{p.price}</span>
                {p.cadence && <span className="text-sm text-eds-muted-fg">{p.cadence}</span>}
              </div>
              {p.governanceStatus && (
                <div className="mt-2 text-[11px] uppercase tracking-wider font-semibold text-eds-muted-fg">
                  Governance: {p.governanceStatus.replace('_', ' ')}
                </div>
              )}
              <ul className="mt-5 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="text-sm text-eds-fg">• {f}</li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="mt-6 inline-flex w-full justify-center px-4 py-2 rounded-eds-md bg-eds-forest text-white text-sm font-medium hover:bg-eds-forest-2"
              >
                Request access
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Videos / media ─────────────────────────────────────────────────────────
export const VideosSection: React.FC<{ items: PublicVideo[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-eds-fg mb-8">Videos & Media</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((v) => (
            <a
              key={v.id}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-eds-surface border border-eds-border rounded-eds-md overflow-hidden hover:shadow-eds-lg transition group"
            >
              <div className="h-44 bg-eds-forest-2 relative">
                {v.thumbnail && (
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover opacity-80" />
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <PlayCircle className="w-12 h-12 text-white drop-shadow" />
                </div>
              </div>
              <div className="p-4 flex items-center justify-between gap-2">
                <h3 className="font-semibold text-eds-fg group-hover:text-eds-emerald-2">{v.title}</h3>
                <PreviewBadge status={v.publishStatus} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Publications / downloads ───────────────────────────────────────────────
export const PublicationsSection: React.FC<{ items: Publication[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-eds-fg mb-8">Publications & Downloads</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} className="bg-eds-surface border border-eds-border rounded-eds-md p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-eds-md bg-eds-surface-2 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-eds-muted-fg" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-eds-fg truncate">{p.title}</h3>
                  <PreviewBadge status={p.publishStatus} />
                </div>
                <div className="text-xs text-eds-muted-fg mt-1">
                  {[p.author, p.year, p.fileType].filter(Boolean).join(' • ')}
                </div>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-eds-emerald-2 font-medium inline-flex items-center gap-1 mt-2"
                  >
                    Download <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── GIS showcase ───────────────────────────────────────────────────────────
export const GisShowcaseSection: React.FC<{ items: GisShowcaseItem[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-8">
          <MapIcon className="w-6 h-6 text-eds-earth" />
          <h2 className="text-3xl font-bold text-eds-fg">GIS Showcase</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((g) => (
            <div key={g.id} className="bg-eds-surface border border-eds-border rounded-eds-md overflow-hidden hover:shadow-eds-md transition">
              <div className="h-40 bg-gradient-to-br from-eds-gold-2 via-eds-forest-2 to-eds-emerald-2">
                {safeUrl(g.previewImageUrl) && (
                  <img src={safeUrl(g.previewImageUrl)} alt={g.title} className="w-full h-full object-cover opacity-90" />
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-eds-fg">{g.title}</h3>
                  <PreviewBadge status={g.publishStatus} />
                </div>
                {g.region && <div className="text-xs text-eds-muted-fg mt-1 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {g.region}</div>}
                <p className="text-sm text-eds-muted-fg mt-2 line-clamp-3">{g.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Office / contact information ───────────────────────────────────────────
export const OfficesSection: React.FC<{ items: OfficeContact[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-8">
          <Building2 className="w-6 h-6 text-eds-fg" />
          <h2 className="text-3xl font-bold text-eds-fg">Our Offices</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((o) => (
            <div key={o.id} className="bg-eds-bg border border-eds-border rounded-eds-md p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-eds-fg">{o.name}</h3>
                <PreviewBadge status={o.publishStatus} />
              </div>
              <p className="text-sm text-eds-muted-fg mt-2">{o.address}</p>
              {o.phone && <div className="text-sm text-eds-muted-fg mt-1">{o.phone}</div>}
              {o.email && <div className="text-sm text-eds-emerald-2 mt-1">{o.email}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Team profiles ──────────────────────────────────────────────────────────
export const TeamSection: React.FC<{ items: TeamProfile[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-8">
          <Users className="w-6 h-6 text-eds-fg" />
          <h2 className="text-3xl font-bold text-eds-fg">Our Team</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {items.map((t) => (
            <div key={t.id} className="bg-eds-surface border border-eds-border rounded-eds-md p-5 text-center">
              <div className="w-20 h-20 rounded-eds-pill bg-eds-surface-2 mx-auto overflow-hidden">
                {t.photoUrl && <img src={t.photoUrl} alt={t.name} className="w-full h-full object-cover" />}
              </div>
              <div className="mt-3 flex justify-center">
                <PreviewBadge status={t.publishStatus} />
              </div>
              <h3 className="font-semibold text-eds-fg mt-2">{t.name}</h3>
              <div className="text-xs text-eds-muted-fg">{t.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Executive / public messages ────────────────────────────────────────────
export const ExecutiveMessagesSection: React.FC<{ items: ExecutiveMessage[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-surface">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-eds-fg mb-8">Executive Messages</h2>
        <div className="space-y-6">
          {items.map((m) => (
            <blockquote key={m.id} className="bg-eds-bg border-l-4 border-eds-emerald p-6 rounded-r-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-eds-pill bg-eds-surface-2 overflow-hidden flex-shrink-0">
                    {m.photoUrl && <img src={m.photoUrl} alt={m.authorName} className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <div className="font-semibold text-eds-fg">{m.authorName}</div>
                    <div className="text-xs text-eds-muted-fg">{m.authorTitle}</div>
                  </div>
                </div>
                <PreviewBadge status={m.publishStatus} />
              </div>
              <p className="mt-4 text-eds-fg italic">"{m.message}"</p>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Environmental awareness / public safety ────────────────────────────────
export const EnvironmentalSection: React.FC<{ items: EnvironmentalNotice[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <section className="py-16 bg-eds-success/12 border-y border-eds-success/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-8">
          <Leaf className="w-6 h-6 text-eds-emerald-2" />
          <h2 className="text-3xl font-bold text-eds-fg">Environment & Public Safety</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((e) => (
            <div key={e.id} className="bg-eds-surface border border-eds-success/30 rounded-eds-md p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-eds-emerald-2 font-semibold inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {e.category}
                </span>
                <PreviewBadge status={e.publishStatus} />
              </div>
              <h3 className="font-semibold text-eds-fg">{e.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-2 line-clamp-4">{e.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
